import React from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Box } from '@mui/material';
import api from '../../services/api';
import { usePreSave } from './PreSaveContext';
import { RichTextToolbar } from './RichTextToolbar';
import { RichTextEditorFrame } from './RichTextEditorFrame';
import { useResolvedMediaSync } from './hooks/useResolvedMediaSync';
import { useDeferredMediaUploads } from './hooks/useDeferredMediaUploads';
import { useRichTextImageLightbox } from './hooks/useRichTextImageLightbox';
import { ImageWithOriginalSrc, LinkWithOriginalHref } from './richTextEditorExtensions';
import ReusableLightbox from '../kanban/projectsKanban/components/imageFiles/components/SharedLighbox';
import './RichTextEditor.css';
import {
  FILE_STORE_UPLOAD_URL,
  IMAGE_STORE_UPLOAD_URL,
  extractAppMediaPath,
  normalizeUploadContextId,
  extractUploadUrl,
  isImageFile,
} from './richTextMediaHelpers';

/** Removes empty `<p>` nodes that TipTap inserts after / inside a trailing blockquote. */
const stripTrailingEmptyParagraphAfterBlockquoteHtml = (html: string) => {
  let stripped = html.replace(
    /(?:<p>(?:\s|&nbsp;|<br\b[^>]*>)*<\/p>)+<\/blockquote>/gi,
    '</blockquote>'
  );
  stripped = stripped.replace(/(<\/blockquote>)(?:<p>(?:\s|&nbsp;|<br\b[^>]*>)*<\/p>)+$/gi, '$1');
  return stripped.trim() === '' ? '<p></p>' : stripped;
};

const isEmptyParagraphNode = (node: {
  type: { name: string };
  content: { size: number };
  textContent: string;
}) => node.type.name === 'paragraph' && (node.content.size === 0 || node.textContent.trim() === '');

const removeTrailingEmptyParagraphAfterBlockquote = (editor: Editor) => {
  const { doc } = editor.state;
  if (doc.childCount < 2) {
    return false;
  }

  const last = doc.lastChild;
  const prev = doc.child(doc.childCount - 2);
  if (!last || !prev || prev.type.name !== 'blockquote' || !isEmptyParagraphNode(last)) {
    return false;
  }

  const from = doc.content.size - last.nodeSize;
  return editor.chain().deleteRange({ from, to: doc.content.size }).run();
};

type RichTextEditorProps = {
  value: string;
  onChange: (content: string) => void;
  label?: React.ReactNode;
  labelId?: string;
  readOnly?: boolean;
  enableMediaUpload?: boolean;
  uploadContextId?: string | number;
  maxImageWidth?: number;
  maxImageHeight?: number;
  /**
   * TipTap often appends an empty trailing paragraph after a blockquote.
   * For reply compose we trim it so no blank line appears under the quote.
   */
  trimTrailingEmptyParagraph?: boolean;
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  labelId,
  readOnly = false,
  enableMediaUpload = false,
  uploadContextId,
  maxImageWidth = 600,
  maxImageHeight = 400,
  trimTrailingEmptyParagraph = false,
}) => {
  const isEditorReadyRef = React.useRef(false);
  const generatedLabelId = React.useId();
  const resolvedLabelId = labelId ?? generatedLabelId;
  const preSave = usePreSave();
  const editorHostRef = React.useRef<HTMLDivElement | null>(null);
  const handleUploadRef = React.useRef<(file: File, type: 'image' | 'file') => void>(() => {});

  const resolvedUploadContextId = React.useMemo(
    () => normalizeUploadContextId(uploadContextId),
    [uploadContextId]
  );

  const canUploadMedia = enableMediaUpload && !readOnly && !!resolvedUploadContextId;

  const uploadDroppedFile = React.useCallback(
    (event: ClipboardEvent | DragEvent, file: File) => {
      if (!canUploadMedia) return false;

      event.preventDefault();
      handleUploadRef.current(file, isImageFile(file) ? 'image' : 'file');
      return true;
    },
    [canUploadMedia]
  );

  const uploadMedia = React.useCallback(
    async (file: File, type: 'image' | 'file'): Promise<string> => {
      if (!resolvedUploadContextId) {
        throw new Error('Upload context ID is missing');
      }

      const uploadEndpoint = type === 'image' ? IMAGE_STORE_UPLOAD_URL : FILE_STORE_UPLOAD_URL;
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(uploadEndpoint, formData, {
        params: { dataUid: resolvedUploadContextId },
      });

      const payload = response?.data as Record<string, unknown>;
      const codeValue = payload?.Code ?? payload?.code;
      const code = Number(codeValue);
      if (Number.isFinite(code) && code < 1) {
        throw new Error('Upload endpoint returned an error response');
      }

      const url = extractUploadUrl(payload);
      if (!url || !extractAppMediaPath(url)) {
        throw new Error('Upload endpoint did not return a media URL');
      }

      return url;
    },
    [resolvedUploadContextId]
  );

  const trimTrailingRef = React.useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure(
        trimTrailingEmptyParagraph
          ? {
              // TipTap 3 StarterKit enables TrailingNode by default; it keeps
              // re-inserting an empty paragraph after blockquote (reply quotes).
              trailingNode: false,
            }
          : {}
      ),
      ImageWithOriginalSrc,
      LinkWithOriginalHref.configure({
        openOnClick: true,
        autolink: true,
      }),
    ],
    content: value,
    editable: !readOnly,
    onCreate: ({ editor: createdEditor }) => {
      isEditorReadyRef.current = true;
      if (trimTrailingEmptyParagraph) {
        removeTrailingEmptyParagraphAfterBlockquote(createdEditor);
      }
    },
    onUpdate: ({ editor: updatedEditor }) => {
      if (!isEditorReadyRef.current) {
        return;
      }

      if (trimTrailingEmptyParagraph && !trimTrailingRef.current) {
        trimTrailingRef.current = true;
        const removed = removeTrailingEmptyParagraphAfterBlockquote(updatedEditor);
        trimTrailingRef.current = false;
        if (removed) {
          // Follow-up onUpdate emits the cleaned HTML.
          return;
        }
      }

      const html = trimTrailingEmptyParagraph
        ? stripTrailingEmptyParagraphAfterBlockquoteHtml(updatedEditor.getHTML())
        : updatedEditor.getHTML();
      onChange(normalizeContentForPersisting(html));
    },
    editorProps: {
      attributes: {
        ...(label ? { 'aria-labelledby': resolvedLabelId } : {}),
      },
      handleDrop(_view, event, _slice, moved) {
        if (moved) return false;

        const files = Array.from(event.dataTransfer?.files ?? []);
        if (!files.length) return false;

        let handled = false;
        for (const file of files) {
          handled = uploadDroppedFile(event, file) || handled;
        }
        return handled;
      },
      handlePaste(_view, event) {
        const clipboard = event.clipboardData;
        if (!clipboard) return false;

        const files = Array.from(clipboard.files ?? []);
        const imageItems = Array.from(clipboard.items ?? []);
        const filesToUpload = files.length
          ? files
          : imageItems
              .map((item) => item.getAsFile())
              .filter((file): file is File => file !== null && isImageFile(file));
        if (!filesToUpload.length) return false;

        let handled = false;
        for (const file of filesToUpload) {
          handled = uploadDroppedFile(event, file) || handled;
        }
        return handled;
      },
    },
  });

  const { normalizeContentForPersisting, scheduleResolvedMediaSync } = useResolvedMediaSync({
    editor,
  });

  const {
    imageInputRef,
    fileInputRef,
    isFlushingPending,
    mediaError,
    hasPendingUploads,
    flushPendingUploads,
    handleImageInputChange,
    handleFileInputChange,
    handleUpload,
  } = useDeferredMediaUploads({
    editor,
    value,
    onChange,
    canUploadMedia,
    uploadContextId,
    uploadMedia,
    normalizeContentForPersisting,
    scheduleResolvedMediaSync,
    maxImageWidth,
    maxImageHeight,
  });

  const { lightboxSlides, lightboxIndex, isLightboxOpen, closeLightbox } = useRichTextImageLightbox(
    {
      editor,
      hostRef: editorHostRef,
    }
  );

  React.useEffect(() => {
    handleUploadRef.current = handleUpload;
  }, [handleUpload]);

  React.useEffect(() => {
    if (!editor) {
      return;
    }

    const currentHtml = normalizeContentForPersisting(editor.getHTML());
    const normalizedValue = trimTrailingEmptyParagraph
      ? stripTrailingEmptyParagraphAfterBlockquoteHtml(value)
      : value;
    const normalizedCurrent = trimTrailingEmptyParagraph
      ? stripTrailingEmptyParagraphAfterBlockquoteHtml(currentHtml)
      : currentHtml;

    if (normalizedValue !== normalizedCurrent) {
      editor.commands.setContent(normalizedValue, { emitUpdate: false });
      if (trimTrailingEmptyParagraph) {
        removeTrailingEmptyParagraphAfterBlockquote(editor);
      }
      void scheduleResolvedMediaSync(true);
    } else if (trimTrailingEmptyParagraph) {
      removeTrailingEmptyParagraphAfterBlockquote(editor);
    }
  }, [
    value,
    editor,
    normalizeContentForPersisting,
    scheduleResolvedMediaSync,
    trimTrailingEmptyParagraph,
  ]);

  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  React.useEffect(() => {
    if (!editor) {
      return;
    }

    scheduleResolvedMediaSync();
    const handleUpdate = () => {
      scheduleResolvedMediaSync();
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, scheduleResolvedMediaSync]);

  React.useEffect(() => {
    if (!preSave) {
      return;
    }

    return preSave.register(flushPendingUploads);
  }, [flushPendingUploads, preSave]);

  if (!editor) {
    return null;
  }

  return (
    <RichTextEditorFrame
      label={label}
      labelId={label ? resolvedLabelId : undefined}
      readOnly={readOnly}
      toolbar={
        !readOnly ? (
          <RichTextToolbar
            editor={editor}
            enableMediaUpload={enableMediaUpload}
            canUploadMedia={canUploadMedia}
            isFlushingPending={isFlushingPending}
            resolvedUploadContextId={resolvedUploadContextId}
            imageInputRef={imageInputRef}
            fileInputRef={fileInputRef}
            onImageInputChange={handleImageInputChange}
            onFileInputChange={handleFileInputChange}
          />
        ) : undefined
      }
    >
      <Box
        ref={editorHostRef}
        sx={{
          p: 2,
          minHeight: '60px',
          '& .ProseMirror': {
            outline: 'none',
            minHeight: '60px',
            color: 'inherit',
            cursor: readOnly ? 'default' : 'text',
            '& p': {
              margin: 0,
              marginBottom: 1,
            },
            '& h1, & h2, & h3': {
              marginTop: 1.5,
              marginBottom: 1,
            },
            '& ul, & ol': {
              paddingLeft: 2,
              marginBottom: 1,
            },
            '& blockquote': {
              borderLeft: (theme) => `3px solid ${theme.palette.divider}`,
              paddingLeft: 2,
              marginLeft: 0,
              fontStyle: 'italic',
            },
            '& pre': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f5f5f5',
              color: 'inherit',
              padding: 1,
              borderRadius: 1,
              overflow: 'auto',
            },
            '& code': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f5f5f5',
              color: 'inherit',
              padding: '2px 4px',
              borderRadius: '3px',
              fontSize: '0.9em',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              marginTop: 0,
              marginBottom: 0,
              borderRadius: 4,
              cursor: 'pointer',
            },
            '& a': {
              color: (theme) => theme.palette.primary.main,
              textDecoration: 'underline',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {mediaError && (
        <Box sx={{ px: 2, pb: 1, color: 'error.main', fontSize: '0.75rem' }}>{mediaError}</Box>
      )}

      <ReusableLightbox
        slides={lightboxSlides}
        isOpen={isLightboxOpen}
        initialIndex={lightboxIndex}
        onClose={closeLightbox}
      />

      {hasPendingUploads && !isFlushingPending && (
        <Box sx={{ px: 2, pb: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
          Media will be uploaded when you click Save.
        </Box>
      )}
    </RichTextEditorFrame>
  );
};
